import axios from 'axios';

export default {
    async bulkOperation(courseId, action, modelType, itemIds, targetType = null, targetId = null) {
        const isTemplate = window.location.pathname.includes('/course-templates/');
        const endpoint = isTemplate ? 'course-templates' : 'courses';

        if (modelType === 'mixed') {
            const courseResponse = await axios.get(`/api/${endpoint}/${courseId}`);
            const courseData = courseResponse.data.data;

            const findItemType = (id, structure) => {
                const sections = structure.sections || [];

                const foundSection = sections.find(section => section.id === id);
                if (foundSection) {
                    return foundSection.section_settings?.type === 'week' ? 'section' : 'section';
                }

                const foundActivity = sections.some(section =>
                    (section.activities || []).some(activity => activity.id === id)
                );

                return foundActivity ? 'activity' : 'activity';
            };

            const sectionIds = [];
            const activityIds = [];

            itemIds.forEach(id => {
                const itemType = findItemType(id, courseData);
                if (itemType === 'section') {
                    sectionIds.push(id);
                } else {
                    activityIds.push(id);
                }
            });

            const promises = [];
            if (sectionIds.length > 0) {
                const sectionPayload = {
                    action,
                    model_type: 'section',
                    item_ids: sectionIds,
                    target_type: targetType,
                    target_id: targetId,
                };
                promises.push(axios.post(`/api/${endpoint}/${courseId}/bulk-items`, sectionPayload));
            }
            if (activityIds.length > 0) {
                const activityPayload = {
                    action,
                    model_type: 'activity',
                    item_ids: activityIds,
                    target_type: targetType,
                    target_id: targetId,
                };
                promises.push(axios.post(`/api/${endpoint}/${courseId}/bulk-items`, activityPayload));
            }

            await Promise.all(promises);
            return { data: { message: `${action} operation successful for mixed items` } };
        }

        const backendModelType = modelType === 'week' || modelType === 'section' ? 'section' : modelType;

        const payload = {
            action,
            model_type: backendModelType,
            item_ids: itemIds,
            target_type: targetType,
            target_id: targetId,
        };

        return axios.post(`/api/${endpoint}/${courseId}/bulk-items`, payload);
    },

    async moveItems(courseId, modelType, itemIds, targetType, targetId) {
        return this.bulkOperation(courseId, 'move', modelType, itemIds, targetType, targetId);
    },

    async copyItems(courseId, modelType, itemIds, targetType, targetId) {
        return this.bulkOperation(courseId, 'copy', modelType, itemIds, targetType, targetId);
    },

    async deleteItems(courseId, modelType, itemIds) {
        return this.bulkOperation(courseId, 'delete', modelType, itemIds);
    },

    async undoMoveItems(courseId, undoData) {
        const { originalPositions } = undoData;
        const itemsByOriginalParentAndType = {};

        // Group items by their original parent and type. If the original parent is the course root,
        // we still want to include them and move them back to the course using target_type 'course'
        // and target_id equal to the courseId.
        originalPositions.forEach(item => {
            const itemType = this.determineItemTypeFromNamespace(item.itemType);

            const parentType = item.originalParentType || 'course';
            const parentId =
                item.originalParentId === null || typeof item.originalParentId === 'undefined'
                    ? courseId
                    : item.originalParentId;

            const groupKey = `${parentType}_${parentId}_${itemType}`;

            if (!itemsByOriginalParentAndType[groupKey]) {
                itemsByOriginalParentAndType[groupKey] = {
                    parentType,
                    parentId,
                    itemType,
                    itemIds: [],
                };
            }
            itemsByOriginalParentAndType[groupKey].itemIds.push(item.itemId);
        });

        const movePromises = Object.values(itemsByOriginalParentAndType).map(group =>
            this.bulkOperation(
                courseId,
                'move',
                group.itemType,
                group.itemIds,
                group.parentType,
                parseInt(group.parentId, 10)
            )
        );

        await Promise.all(movePromises);
        return { success: true };
    },

    determineItemTypeFromNamespace(namespace) {
        switch (namespace) {
            case 'Activities':
            case 'ActivityTemplates':
                return 'activity';
            case 'Sections':
            case 'SectionTemplates':
                return 'section';
            default:
                return 'activity';
        }
    },
};
